import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { RANK_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import type { ReorderRow } from '@atlassian/jira-native-issue-table/src/common/types.tsx';
import type {
	useIssuesRankerMutation,
	useIssuesRankerMutation$data,
	JiraRankMutationEdge,
} from '@atlassian/jira-relay/src/__generated__/useIssuesRankerMutation.graphql';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { JiraIssueAri } from '@atlassian/ari/jira';
import { PACKAGE_NAME, TEAM_NAME, ANALYTICS_SOURCE } from '../../common/constants.tsx';
import { useDraggableRowsConnectionId } from '../../controllers/draggable-rows/context.tsx';
import { RE_RANKING_EXPERIENCE } from './constants.tsx';
import { getFlagDetails } from './utils.tsx';
import messages from './messages.tsx';

const getRankMutationEdge = (edge: Edge): JiraRankMutationEdge | null => {
	switch (edge) {
		case 'top':
			return 'TOP';
		case 'bottom':
			return 'BOTTOM';
		default:
			// Unsupported edge
			return null;
	}
};

export const useIssuesRanker = () => {
	const [issueConnectionId] = useDraggableRowsConnectionId();
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onExperienceStart = useExperienceStart({
		experience: RE_RANKING_EXPERIENCE,
		analyticsSource: ANALYTICS_SOURCE,
	});

	const onExperienceFail = useExperienceFail({
		experience: RE_RANKING_EXPERIENCE,
	});

	const onExperienceSuccess = useExperienceSuccess({
		experience: RE_RANKING_EXPERIENCE,
	});

	const [rankIssuesMutation] = useMutation<useIssuesRankerMutation>(graphql`
		mutation useIssuesRankerMutation($rankInput: JiraRankMutationInput!) {
			jira {
				rankIssues(rankInput: $rankInput) {
					success
					errors {
						message
						extensions {
							statusCode
							errorType
						}
					}
				}
			}
		}
	`);

	const showErrorFlag = useCallback(() => {
		showFlag({
			type: 'error',
			title: fg('jira-issue-terminology-refresh-m3')
				? messages.reorderErrorTitleIssueTermRefresh
				: messages.reorderErrorTitle,
			description: messages.reorderErrorDescription,
			isAutoDismiss: true,
			messageId:
				'native-issue-table.services.rank-mutation.show-flag.error.use-issues-ranker.show-flag.error',
			messageType: 'transactional',
		});
	}, [showFlag]);

	const reorderIssuesUpdater = useCallback(
		(store: RecordSourceSelectorProxy, startIndex: number, finishIndex: number) => {
			const issueConnection = store.get(issueConnectionId);
			if (!issueConnection) return;

			const edges = issueConnection.getLinkedRecords('edges');
			if (!edges) return;

			const reorderedEdges = reorder({
				list: edges,
				startIndex,
				finishIndex,
			});

			issueConnection.setLinkedRecords(reorderedEdges, 'edges');
		},
		[issueConnectionId],
	);

	const handleError = useCallback(
		(error: MutationError | Error) => {
			fireErrorAnalytics({
				meta: {
					id: 'commitRankIssuesMutationError',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			onExperienceFail(PACKAGE_NAME, error);
			showErrorFlag();
		},
		[onExperienceFail, showErrorFlag],
	);

	const rankIssues = useCallback<ReorderRow>(
		(payload) => {
			const { source, destination, edge } = payload;

			const rankMutationEdge = getRankMutationEdge(edge);

			onExperienceStart();

			// 1event does not currently support bulk analytics
			if (fg('jira_timeline_analytics_harmonization')) {
				if (payload.source.issueIds.length === 1) {
					getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(RANK_CF_TYPE, {
						analytics: createAnalyticsEvent({}),
						attributes: {
							issueId: JiraIssueAri.parse(payload.source.issueIds[0]).issueId,
							fieldType: FIELD_TYPE_MAP[RANK_CF_TYPE],
						},
					});
				}
			}

			if (rankMutationEdge == null) {
				showErrorFlag();
				fireErrorAnalytics({
					meta: {
						id: 'commitRankIssuesError',
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					error: new Error(`Edge "${edge}" is unsupported in JiraMutation.rankIssues`),
					sendToPrivacyUnsafeSplunk: true,
				});

				return;
			}

			rankIssuesMutation({
				variables: {
					rankInput: {
						issues: source.issueIds,
						relativeToIssue: destination.relativeToIssueId,
						edge: rankMutationEdge,
					},
				},
				optimisticUpdater(store) {
					reorderIssuesUpdater(store, source.rowIndex, destination.rowIndex);
				},
				updater(store, data) {
					// Rollback update if mutation fails
					if (!data?.jira?.rankIssues?.success) return;

					reorderIssuesUpdater(store, source.rowIndex, destination.rowIndex);
				},
				onCompleted(data: useIssuesRankerMutation$data) {
					const { errors } = data?.jira?.rankIssues ?? {};

					if (errors && errors.length > 0) {
						const error = new MutationError(errors[0]);
						const statusCode = error.statusCode;

						const clientSideErrorMessage = getFlagDetails(statusCode);

						if (clientSideErrorMessage) {
							showFlag(clientSideErrorMessage);
							onExperienceSuccess();
							return;
						}

						handleError(error);
						return;
					}

					if (!data.jira?.rankIssues?.success) {
						const error = new Error(
							errors?.[0]?.message ?? 'Failed to reorder issues: Unknown Error occurred',
						);
						handleError(error);
						return;
					}

					onExperienceSuccess();

					fireTrackAnalytics(
						createAnalyticsEvent({}),
						'issue updated',
						'issueTableUpdateIssueRank',
					);
				},
				onError(error: Error) {
					handleError(error);
				},
			});
		},
		[
			createAnalyticsEvent,
			handleError,
			onExperienceStart,
			onExperienceSuccess,
			rankIssuesMutation,
			reorderIssuesUpdater,
			showErrorFlag,
			showFlag,
		],
	);

	return rankIssues;
};
